import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Search, Image as ImageIcon, FileText, Download } from "lucide-react";

const STATUS_STYLE = {
  completed: "bg-emerald-100 text-emerald-700",
  failed: "bg-rose-100 text-rose-700",
  timed_out: "bg-amber-100 text-amber-700",
  generating: "bg-indigo-100 text-indigo-700",
  saving: "bg-indigo-100 text-indigo-700",
  pending: "bg-zinc-100 text-zinc-600",
};
const fmtDT = (s) => (s ? new Date(s).toLocaleString() : "—");
const cost = (j) => {
  if (j.actual_cost_usd) return `$${Number(j.actual_cost_usd).toFixed(4)} (actual)`;
  if (j.est_cost_usd != null) return `~$${Number(j.est_cost_usd).toFixed(4)} (est.)`;
  return "Unavailable";
};

export function AdminAiGenerations({ initialBusinessId = "" }) {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [genType, setGenType] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [businessId, setBusinessId] = useState(initialBusinessId);
  const [detail, setDetail] = useState(null);
  const pageSize = 20;

  const load = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const params = { page: p, page_size: pageSize };
      if (q.trim()) params.q = q.trim();
      if (status !== "all") params.status = status;
      if (genType !== "all") params.gen_type = genType;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (businessId) params.business_id = businessId;
      const r = await api.get("/admin/ai-generations", { params });
      setItems(r.data.items || []); setSummary(r.data.summary); setTotal(r.data.total); setPage(r.data.page);
    } finally { setLoading(false); }
  }, [q, status, genType, dateFrom, dateTo, businessId, page]);

  useEffect(() => { load(1); /* eslint-disable-next-line */ }, [status, genType, businessId]);
  useEffect(() => { load(1); /* eslint-disable-next-line */ }, []);

  const openDetail = async (id) => {
    try { const r = await api.get(`/admin/ai-generations/${id}`); setDetail(r.data); } catch { /* noop */ }
  };
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div data-testid="admin-ai-generations">
      {summary && (
        <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-6" data-testid="ai-gen-summary">
          {[["Total", summary.total], ["Completed", summary.completed], ["Failed", summary.failed], ["Timed out", summary.timed_out], ["Active", summary.active]].map(([l, v]) => (
            <div key={l} className="rounded-xl border border-zinc-200 bg-white p-3">
              <p className="text-xs text-zinc-500">{l}</p><p className="text-lg font-bold text-zinc-900">{v}</p>
            </div>
          ))}
          <div className="rounded-xl border border-zinc-200 bg-white p-3">
            <p className="text-xs text-zinc-500">Est. cost (completed)</p>
            <p className="text-lg font-bold text-zinc-900">${Number(summary.est_cost_usd || 0).toFixed(3)}</p>
          </div>
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-end gap-2">
        <div className="min-w-[180px] flex-1">
          <Label className="text-xs">Search (business, user or job ID)</Label>
          <div className="mt-1 flex gap-1">
            <Input data-testid="ai-gen-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(1)} placeholder="e.g. The Ivy Reading" className="h-9" />
            <Button data-testid="ai-gen-search-btn" size="sm" onClick={() => load(1)} className="h-9 rounded-full"><Search className="h-4 w-4" /></Button>
          </div>
        </div>
        <div><Label className="text-xs">Type</Label>
          <Select value={genType} onValueChange={setGenType}><SelectTrigger data-testid="ai-gen-type" className="mt-1 h-9 w-32"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="image">Image</SelectItem><SelectItem value="copy">Copy</SelectItem></SelectContent></Select>
        </div>
        <div><Label className="text-xs">Status</Label>
          <Select value={status} onValueChange={setStatus}><SelectTrigger data-testid="ai-gen-status" className="mt-1 h-9 w-36"><SelectValue /></SelectTrigger>
            <SelectContent>{["all", "completed", "failed", "timed_out", "generating", "saving", "pending"].map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent></Select>
        </div>
        <div><Label className="text-xs">From</Label><Input data-testid="ai-gen-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1 h-9 w-36" /></div>
        <div><Label className="text-xs">To</Label><Input data-testid="ai-gen-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1 h-9 w-36" /></div>
        <Button size="sm" variant="outline" onClick={() => load(1)} className="h-9 rounded-full">Apply dates</Button>
        {businessId && <Button size="sm" variant="outline" data-testid="ai-gen-clear-business" onClick={() => setBusinessId("")} className="h-9 rounded-full">Clear business filter</Button>}
      </div>

      {loading ? <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div> : (
        <div className="overflow-x-auto rounded-2xl border border-zinc-200 bg-white">
          <table className="w-full min-w-[720px] text-sm" data-testid="ai-gen-table">
            <thead className="border-b border-zinc-100 text-left text-xs uppercase text-zinc-400">
              <tr><th className="p-3">Business</th><th className="p-3">Type</th><th className="p-3">Status</th><th className="p-3">Requested</th><th className="p-3">Duration</th><th className="p-3">Credit</th><th className="p-3">Cost</th></tr>
            </thead>
            <tbody>
              {items.length === 0 ? <tr><td colSpan={7} className="p-6 text-center text-zinc-400">No generations found.</td></tr>
                : items.map((j) => (
                  <tr key={j.id} data-testid={`ai-gen-row-${j.id}`} onClick={() => openDetail(j.id)} className="cursor-pointer border-b border-zinc-50 hover:bg-zinc-50">
                    <td className="p-3"><p className="font-medium text-zinc-900">{j.business_name || "—"}</p><p className="text-xs text-zinc-400">{j.initiating_user_name}</p></td>
                    <td className="p-3">{j.gen_type === "image" ? <span className="inline-flex items-center gap-1 text-indigo-600"><ImageIcon className="h-3.5 w-3.5" /> Image</span> : <span className="inline-flex items-center gap-1 text-zinc-600"><FileText className="h-3.5 w-3.5" /> Copy</span>}</td>
                    <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_STYLE[j.status] || "bg-zinc-100"}`}>{j.status.replace("_", " ")}</span></td>
                    <td className="p-3 text-xs text-zinc-500">{fmtDT(j.created_at)}</td>
                    <td className="p-3 text-xs text-zinc-500">{j.duration_ms ? `${(j.duration_ms / 1000).toFixed(1)}s` : "—"}</td>
                    <td className="p-3 text-xs">{j.credit_source ? <span className="capitalize">{j.credit_source} · {j.credit_state}</span> : "—"}</td>
                    <td className="p-3 text-xs text-zinc-500">{cost(j)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-3 flex items-center justify-between text-sm text-zinc-500">
        <span>{total} generation{total === 1 ? "" : "s"}</span>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => load(page - 1)} className="rounded-full">Prev</Button>
          <span>{page} / {totalPages}</span>
          <Button size="sm" variant="outline" data-testid="ai-gen-next" disabled={page >= totalPages} onClick={() => load(page + 1)} className="rounded-full">Next</Button>
        </div>
      </div>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="ai-gen-detail">
          <DialogHeader><DialogTitle>Generation detail</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <Field l="Business" v={detail.business_name} />
                <Field l="Initiated by" v={detail.initiating_user_name} />
                <Field l="Type" v={detail.gen_type} />
                <Field l="Status" v={detail.status?.replace("_", " ")} />
                <Field l="Provider / model" v={`${detail.provider || "—"} · ${detail.model || "—"}`} />
                <Field l="Credit" v={detail.credit_source ? `${detail.credit_source} · ${detail.credit_state}` : "Not charged"} />
                <Field l="Requested" v={fmtDT(detail.created_at)} />
                <Field l="Completed" v={fmtDT(detail.completed_at)} />
                <Field l="Duration" v={detail.duration_ms ? `${(detail.duration_ms / 1000).toFixed(1)}s` : "—"} />
                <Field l="Cost" v={cost(detail)} />
                <Field l="Job ID" v={detail.id} />
              </div>

              {detail.gen_type === "image" && (
                detail.output_deleted ? <p className="rounded-lg bg-zinc-100 px-3 py-2 text-xs text-zinc-500" data-testid="ai-gen-output-deleted">Output deleted by the business.</p>
                  : detail.image_url ? (
                    <div>
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${detail.image_url}`} alt="" className="w-full rounded-xl border border-zinc-200" />
                      <a href={`${process.env.REACT_APP_BACKEND_URL}${detail.image_url}`} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs text-emerald-700"><Download className="h-3.5 w-3.5" /> Open image</a>
                    </div>
                  ) : null
              )}

              {detail.gen_type === "copy" && detail.copy_pack && (
                <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3 text-xs">
                  <p className="font-semibold text-zinc-700">Main caption</p>
                  <p className="mt-1 whitespace-pre-line text-zinc-600">{detail.copy_pack.main_caption}</p>
                </div>
              )}

              {detail.prompt && <Field l="Prompt" v={detail.prompt} block />}
              {detail.error && <div className="rounded-lg border border-rose-200 bg-rose-50 p-2 text-xs text-rose-700"><b>Error:</b> {detail.error}</div>}

              {Array.isArray(detail.status_history) && detail.status_history.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-zinc-500">Status history</p>
                  <div className="mt-1 space-y-0.5">
                    {detail.status_history.map((h, i) => <p key={i} className="text-xs text-zinc-500"><span className="capitalize font-medium text-zinc-700">{h.status.replace("_", " ")}</span> · {fmtDT(h.at)}</p>)}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ l, v, block }) {
  return (
    <div className={block ? "col-span-2" : ""}>
      <p className="text-xs text-zinc-400">{l}</p>
      <p className="break-words text-sm text-zinc-800">{v || "—"}</p>
    </div>
  );
}
